import { CircularProgress, CircularProgressProps, makeStyles, Typography, TypographyProps } from '@material-ui/core';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: '0.938rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    minHeight: '1.5em',
    transition: 'opacity 0.3s ease',
  },
  loadingSpinner: {
    marginBottom: theme.spacing(3),
  },
}));

interface DynamicTextProps extends TypographyProps {
  texts: string[];
  isRunning?: boolean;
  spinner?: boolean;
  spinnerProps?: CircularProgressProps;
}

const DynamicText: FC<DynamicTextProps> = ({ texts, isRunning, spinner, spinnerProps, className, ...props }) => {
  const classes = useStyles();

  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Rotate loading messages every 4 seconds, stop at the last one
  useEffect(() => {
    if (!isRunning) {
      setLoadingMessageIndex(0); // Reset when not loading
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => {
        // Stop at the last message, don't loop
        if (prev >= texts.length - 1) {
          return prev;
        }
        return prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isRunning, texts.length]);

  if (!isRunning) return null;

  return (
    <div className={classes.container}>
      {!!spinner && (
        <CircularProgress
          className={clsx(classes.loadingSpinner, spinnerProps?.className)}
          size={spinnerProps?.size || 48}
          {...spinnerProps}
        />
      )}
      <Typography className={clsx(classes.loadingText, className)} {...props}>
        {texts[loadingMessageIndex]}
      </Typography>
    </div>
  );
};

export default DynamicText;
